import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Pencil, Check, X, Tags } from "lucide-react";
import { toast } from "sonner";
import { VOUCHER_ICONS, VoucherTypeIcon, useVoucherTypes, reloadVoucherTypes } from "../../lib/voucherTypes";

const IconPicker = ({ value, onChange, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="h-9 w-9 shrink-0 rounded-lg border border-sky-200 bg-white flex items-center justify-center text-sky-700 hover:bg-sky-50" data-testid={testId}>
        <VoucherTypeIcon icon={value} />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-72 p-2">
      <div className="grid grid-cols-7 gap-1">
        {Object.keys(VOUCHER_ICONS).map((k) => (
          <button key={k} type="button" title={k} onClick={() => onChange(k)} data-testid={`icon-option-${k}`}
            className={`h-8 w-8 rounded-md flex items-center justify-center hover:bg-sky-100 ${value === k ? "bg-sky-600 text-white hover:bg-sky-600" : "text-sky-700"}`}>
            <VoucherTypeIcon icon={k} className="w-4 h-4" />
          </button>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

const emptyForm = { label: "", icon: "file-text", keywords: "" };

const TypeRow = ({ t, api, axiosConfig }) => {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      await axios.put(`${api}/voucher-types/${t.id}`, edit, axiosConfig);
      toast.success("Tipo actualizado");
      setEdit(null);
      reloadVoucherTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar el tipo "${t.label}"?`)) return;
    try {
      await axios.delete(`${api}/voucher-types/${t.id}`, axiosConfig);
      toast.success("Tipo eliminado");
      reloadVoucherTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };
  if (edit) {
    return (
      <div className="flex items-center gap-2 py-2" data-testid={`voucher-type-edit-${t.key}`}>
        <IconPicker value={edit.icon} onChange={(icon) => setEdit((f) => ({ ...f, icon }))} testId={`voucher-type-icon-${t.key}`} />
        <Input value={edit.label} onChange={(e) => setEdit((f) => ({ ...f, label: e.target.value }))} className="h-9 border-sky-200 w-40" data-testid={`voucher-type-label-${t.key}`} />
        <Input value={edit.keywords} onChange={(e) => setEdit((f) => ({ ...f, keywords: e.target.value }))} placeholder="palabras clave, separadas por coma" className="h-9 border-sky-200 flex-1" />
        <Button size="sm" onClick={save} className="btn-sky text-white h-9" data-testid={`voucher-type-save-${t.key}`}><Check className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={() => setEdit(null)} className="h-9 border-sky-200"><X className="w-4 h-4" /></Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`voucher-type-${t.key}`}>
      <span className="h-9 w-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center shrink-0"><VoucherTypeIcon icon={t.icon} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sky-900 text-sm">{t.label}</p>
        <p className="text-xs text-sky-600/70 truncate">{(t.keywords || []).join(", ") || "Sin palabras clave"}</p>
      </div>
      <button type="button" onClick={() => setEdit({ label: t.label, icon: t.icon, keywords: (t.keywords || []).join(", ") })} className="p-1.5 rounded-md text-sky-600 hover:bg-sky-100" data-testid={`voucher-type-edit-btn-${t.key}`}><Pencil className="w-4 h-4" /></button>
      {t.key !== "otros" && (
        <button type="button" onClick={remove} className="p-1.5 rounded-md text-red-500 hover:bg-red-50" data-testid={`voucher-type-delete-${t.key}`}><Trash2 className="w-4 h-4" /></button>
      )}
    </div>
  );
};

export const VoucherTypesCard = ({ api, axiosConfig }) => {
  const { types } = useVoucherTypes();
  const [form, setForm] = useState(emptyForm);

  const create = async () => {
    if (!form.label.trim()) return toast.error("Poné un nombre");
    try {
      await axios.post(`${api}/voucher-types`, form, axiosConfig);
      toast.success("Tipo creado");
      setForm(emptyForm);
      reloadVoucherTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear");
    }
  };

  return (
    <Card className="glass-card border-0 mb-6" data-testid="voucher-types-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2"><Tags className="w-5 h-5" /> Tipos de servicio (vouchers)</CardTitle>
        <CardDescription>Nombre, ícono y palabras clave para reconocer el tipo por el nombre del archivo.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="voucher-type-new">
          <IconPicker value={form.icon} onChange={(icon) => setForm((f) => ({ ...f, icon }))} testId="voucher-type-new-icon" />
          <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Nuevo tipo (ej. Bodega)" className="h-9 border-sky-200 w-48" data-testid="voucher-type-new-label" />
          <Input value={form.keywords} onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))} placeholder="palabras clave, separadas por coma" className="h-9 border-sky-200 flex-1 min-w-[180px]" data-testid="voucher-type-new-keywords" />
          <Button size="sm" onClick={create} className="btn-sky text-white h-9" data-testid="voucher-type-new-save"><Plus className="w-4 h-4 mr-1" /> Agregar</Button>
        </div>
        <div className="divide-y divide-sky-100 max-h-[420px] overflow-y-auto pr-1">
          {types.map((t) => <TypeRow key={t.id} t={t} api={api} axiosConfig={axiosConfig} />)}
        </div>
      </CardContent>
    </Card>
  );
};
